import React from "react";
import { Col, Row, TabPane } from "reactstrap";
import { LTA_FIELDS, LTAFieldKey, FieldMapping } from "./ltaImportFields";

export type LTAsImportWizardTabFieldMappingProps = {
  activeTab: number;
  toggleTab: (tab: number) => void;
  csvHeaders: string[];
  fieldMappings: FieldMapping;
  setFieldMappings: React.Dispatch<React.SetStateAction<FieldMapping>>;
};

const LTAsImportWizardTabFieldMapping: React.FC<LTAsImportWizardTabFieldMappingProps> = ({
  activeTab, toggleTab, csvHeaders, fieldMappings, setFieldMappings,
}) => {
  const handleChange = (key: LTAFieldKey, value: string) => {
    setFieldMappings((prev) => {
      const next = { ...prev };
      if (value) { next[key] = value; } else { delete next[key]; }
      return next;
    });
  };

  const mappedCount = Object.keys(fieldMappings).length;
  const requiredMapped = !!fieldMappings["ContractTitle"];

  return (
    <TabPane tabId={2}>
      <div className="d-flex align-items-start gap-3 mb-4 pb-3" style={{ borderBottom: "1px solid #eef0f4" }}>
        <div className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 38, height: 38, background: "#eef2ff" }}>
          <i className="ri-git-merge-line" style={{ fontSize: 18, color: "#4361ee" }} />
        </div>
        <div className="flex-grow-1">
          <h5 className="mb-0 fw-semibold">Field Mapping</h5>
          <p className="text-muted mb-0 mt-1">Map your file's columns to LTA fields. Headers have been automatically matched — review and adjust as needed.</p>
        </div>
        <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 fs-12 flex-shrink-0 align-self-center">Step 2 of 4</span>
      </div>

      {csvHeaders.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="ri-file-warning-line fs-1 mb-2 d-block" />No CSV headers found. Please go back and upload a valid file.
        </div>
      ) : (
        <Row>
          <Col lg={12}>
            <div className="d-flex align-items-center gap-3 rounded-2 px-3 py-2 mb-3" style={{ background: "#f8f9fc", border: "1px solid #e9ecef" }}>
              <div className="d-flex align-items-center gap-2">
                <span><strong>{mappedCount}</strong> of <strong>{LTA_FIELDS.length}</strong> fields mapped</span>
              </div>
              <div className="vr mx-1" style={{ height: 20 }} />
              <span style={{ color: "#64748b" }}>Fields marked <span className="text-danger fw-semibold">*</span> are required</span>
              {!requiredMapped && (
                <>
                  <div className="vr mx-1" style={{ height: 20 }} />
                  <span className="d-flex align-items-center gap-1" style={{ fontSize: 13, color: "#dc2626" }}>
                    <i className="ri-error-warning-line" />Contract Title must be mapped to continue
                  </span>
                </>
              )}
            </div>

            <div className="rounded-3 border overflow-hidden">
              <table className="table table-hover align-middle mb-0">
                <thead style={{ background: "#f1f4f9" }}>
                  <tr>
                    <th className="py-3 ps-3" style={{ width: "38%", fontWeight: 600, color: "#374151", borderBottom: "2px solid #dee2e6" }}>LTA Field</th>
                    <th className="py-3" style={{ fontWeight: 600, color: "#374151", borderBottom: "2px solid #dee2e6" }}>CSV Column</th>
                    <th className="py-3 pe-3 text-center" style={{ width: 110, fontWeight: 600, color: "#374151", borderBottom: "2px solid #dee2e6" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {LTA_FIELDS.map((field) => {
                    const mapped = !!fieldMappings[field.key];
                    return (
                      <tr key={field.key} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td className="ps-3 py-2">
                          <span className="fw-medium" style={{ color: "#1e293b" }}>{field.label}</span>
                          {field.required && <span className="text-danger ms-1 fw-bold">*</span>}
                        </td>
                        <td className="py-2">
                          <select className="form-select form-select-sm" style={{ maxWidth: 280 }}
                            value={fieldMappings[field.key] ?? ""} onChange={(e) => handleChange(field.key, e.target.value)}>
                            <option value="">— Not mapped —</option>
                            {csvHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                          </select>
                        </td>
                        <td className="text-center py-2 pe-3">
                          {mapped
                            ? <span className="badge rounded-pill d-inline-flex align-items-center gap-1" style={{ background: "#dcfce7", color: "#16a34a", fontSize: 12, fontWeight: 600, padding: "4px 10px" }}><i className="ri-check-line" />Mapped</span>
                            : <span className="badge rounded-pill" style={{ background: "#f1f5f9", color: "#94a3b8", fontSize: 12, fontWeight: 600, padding: "4px 10px" }}>Skipped</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Col>
        </Row>
      )}

      <div className="d-flex align-items-center gap-3 mt-4 pt-3" style={{ borderTop: "1px solid #eef0f4" }}>
        <button type="button" className="btn btn-light btn-label" onClick={() => toggleTab(activeTab - 1)}>
          <i className="ri-arrow-left-line label-icon align-middle fs-16 me-2" />Back
        </button>
        <button type="button" className="btn btn-primary btn-label right ms-auto"
          disabled={!requiredMapped} onClick={() => toggleTab(activeTab + 1)}
          title={!requiredMapped ? "Contract Title must be mapped before continuing" : undefined}>
          <i className="ri-eye-line label-icon align-middle fs-16 ms-2" />Preview Data
        </button>
      </div>
    </TabPane>
  );
};

export default LTAsImportWizardTabFieldMapping;
